import { api, http } from '../client';
import type { PageParams } from '@/types/api';
import type { UUID } from '@/types/domain';
import type {
  HrDashboard,
  HrSettings,
  HrSettingsInput,
  LeaveBalance,
  LeaveBalanceAdjustmentInput,
  LeaveDecisionInput,
  LeaveRequest,
  LeaveRequestInput,
  LeaveRequestStatus,
  LeaveSummary,
  LeaveType,
  LeaveTypeInput,
  LeaveTypeStatus,
  Loan,
  LoanInput,
  LoanStatus,
  PayrollAdjustment,
  PayrollAdjustmentInput,
  PayrollPeriod,
  PayrollPeriodInput,
  PayrollRunResult,
  PayrollSummary,
  Payslip,
  SalaryAssignment,
  SalaryAssignmentInput,
  SalaryComponent,
  SalaryComponentInput,
  SalaryStatus,
  SalaryStructure,
  SalaryStructureInput,
} from '@/types/hr';

// ------------------------------------------------------------------ settings

export const hrSettingsApi = {
  get: () => api.get<HrSettings>('/hr/settings'),
  update: (payload: HrSettingsInput) => api.put<HrSettings>('/hr/settings', payload),
};

// --------------------------------------------------------------------- leave

export const leaveTypesApi = {
  list: (activeOnly = false) => api.get<LeaveType[]>('/leave-types', { params: { activeOnly } }),
  get: (id: UUID) => api.get<LeaveType>(`/leave-types/${id}`),
  create: (payload: LeaveTypeInput) => api.post<LeaveType>('/leave-types', payload),

  /** The code cannot change once created; an import or payroll rule names it. */
  update: (id: UUID, payload: LeaveTypeInput) => api.put<LeaveType>(`/leave-types/${id}`, payload),

  /** Retired, never deleted — years of balances and requests name their type. */
  setStatus: (id: UUID, status: LeaveTypeStatus) =>
    api.patch<LeaveType>(`/leave-types/${id}/status`, undefined, { params: { status } }),
};

export const leaveBalancesApi = {
  /** The caller's own. Takes no identifier: resolved from the account. */
  mine: (year?: number) => api.get<LeaveBalance[]>('/leave-balances/me', { params: { year } }),

  forEmployee: (employeeId: UUID, year?: number) =>
    api.get<LeaveBalance[]>(`/leave-balances/${employeeId}`, { params: { year } }),

  /** Refused when it would go below what is already taken or pending. */
  adjust: (payload: LeaveBalanceAdjustmentInput) =>
    api.post<LeaveBalance>('/leave-balances/adjust', payload),
};

export const leaveRequestsApi = {
  list: (
    params: PageParams & {
      employeeId?: UUID;
      leaveTypeId?: UUID;
      status?: LeaveRequestStatus[];
      from?: string;
      to?: string;
    },
  ) => api.getPage<LeaveRequest>('/leave-requests', { params }),

  get: (id: UUID) => api.get<LeaveRequest>(`/leave-requests/${id}`),

  mine: (params: PageParams) => api.getPage<LeaveRequest>('/leave-requests/me', { params }),

  /** Scoped to the caller's own direct reports, not the whole school. */
  awaitingMe: () => api.get<LeaveRequest[]>('/leave-requests/awaiting-me'),

  onDate: (date?: string) => api.get<LeaveRequest[]>('/leave-requests/on-date', { params: { date } }),

  /** Any employeeId in the payload is ignored: the employee comes from the account. */
  submitForSelf: (payload: LeaveRequestInput) =>
    api.post<LeaveRequest>('/leave-requests/me', payload),

  /** HR filing on somebody's behalf. Notice periods are not enforced here. */
  submitFor: (payload: LeaveRequestInput) => api.post<LeaveRequest>('/leave-requests', payload),

  managerDecision: (id: UUID, payload: LeaveDecisionInput) =>
    api.post<LeaveRequest>(`/leave-requests/${id}/manager-decision`, payload),

  hrDecision: (id: UUID, payload: LeaveDecisionInput) =>
    api.post<LeaveRequest>(`/leave-requests/${id}/hr-decision`, payload),

  cancel: (id: UUID, reason: string) =>
    api.post<LeaveRequest>(`/leave-requests/${id}/cancel`, { reason }),

  /** Moves approved leave whose last day has passed to TAKEN. */
  closeOut: () => api.post<number>('/leave-requests/close-out'),

  /**
   * Attach the supporting document. Requester, their supervisor or HR only,
   * and only while the request awaits a decision; replaces any earlier upload.
   * Content-Type is left undefined so the browser sets multipart/form-data
   * with its boundary.
   */
  attachDocument: async (id: UUID, file: File): Promise<LeaveRequest> => {
    const form = new FormData();
    form.append('file', file);
    const response = await http.post<{ data: LeaveRequest }>(
      `/leave-requests/${id}/document`,
      form,
      { headers: { 'Content-Type': undefined } },
    );
    return response.data.data;
  },

  /** For the requester, their supervisor and HR. */
  downloadDocument: (id: UUID, fallbackName = 'leave-document') =>
    download(`/leave-requests/${id}/document`, {}, fallbackName),
};

// -------------------------------------------------------------------- salary

export const salaryComponentsApi = {
  list: (activeOnly = false) =>
    api.get<SalaryComponent[]>('/salary-components', { params: { activeOnly } }),
  get: (id: UUID) => api.get<SalaryComponent>(`/salary-components/${id}`),
  create: (payload: SalaryComponentInput) =>
    api.post<SalaryComponent>('/salary-components', payload),

  /** Neither the code nor the earning/deduction side can change. */
  update: (id: UUID, payload: SalaryComponentInput) =>
    api.put<SalaryComponent>(`/salary-components/${id}`, payload),

  /** A statutory component cannot be retired at all. */
  setStatus: (id: UUID, status: SalaryStatus) =>
    api.patch<SalaryComponent>(`/salary-components/${id}/status`, undefined, { params: { status } }),
};

export const salaryStructuresApi = {
  list: (activeOnly = false) =>
    api.get<SalaryStructure[]>('/salary-structures', { params: { activeOnly } }),
  get: (id: UUID) => api.get<SalaryStructure>(`/salary-structures/${id}`),

  /** Sending no components fills the package with every active one at its default. */
  create: (payload: SalaryStructureInput) =>
    api.post<SalaryStructure>('/salary-structures', payload),

  /** A component list replaces the lines wholesale; omitting it leaves them alone. */
  update: (id: UUID, payload: SalaryStructureInput) =>
    api.put<SalaryStructure>(`/salary-structures/${id}`, payload),

  setStatus: (id: UUID, status: SalaryStatus) =>
    api.patch<SalaryStructure>(`/salary-structures/${id}/status`, undefined, { params: { status } }),
};

export const salaryAssignmentsApi = {
  history: (employeeId: UUID) =>
    api.get<SalaryAssignment[]>(`/salary-assignments/employees/${employeeId}`),

  current: (employeeId: UUID) =>
    api.get<SalaryAssignment>(`/salary-assignments/employees/${employeeId}/current`),

  /** One call for both appointing and raising: it closes what came before. */
  assign: (payload: SalaryAssignmentInput) =>
    api.post<SalaryAssignment>('/salary-assignments', payload),

  /** The basic and the reason only — dates and package are not editable. */
  correct: (id: UUID, payload: { basicSalary?: number; reason: string }) =>
    api.patch<SalaryAssignment>(`/salary-assignments/${id}`, payload),
};

// ------------------------------------------------------------------- payroll

export const payrollApi = {
  list: (params: PageParams) => api.getPage<PayrollPeriod>('/payroll-periods', { params }),

  /** At most one per school; a partial unique index guarantees it. */
  open: () => api.get<PayrollPeriod[]>('/payroll-periods/open'),

  get: (id: UUID) => api.get<PayrollPeriod>(`/payroll-periods/${id}`),

  openPeriod: (payload: PayrollPeriodInput) =>
    api.post<PayrollPeriod>('/payroll-periods', payload),

  /** Safe to run again: it replaces the previous run rather than adding to it. */
  calculate: (id: UUID) => api.post<PayrollRunResult>(`/payroll-periods/${id}/calculate`),

  /** The point of no return — figures are fixed after this. */
  approve: (id: UUID) => api.post<PayrollPeriod>(`/payroll-periods/${id}/approve`),

  markPaid: (id: UUID, reference?: string) =>
    api.post<PayrollPeriod>(`/payroll-periods/${id}/pay`, { reference }),

  cancel: (id: UUID, reason: string) =>
    api.post<PayrollPeriod>(`/payroll-periods/${id}/cancel`, { reason }),
};

export const payslipsApi = {
  forPeriod: (periodId: UUID, params: PageParams) =>
    api.getPage<Payslip>('/payslips', { params: { ...params, periodId } }),

  forEmployee: (employeeId: UUID, params: PageParams) =>
    api.getPage<Payslip>('/payslips', { params: { ...params, employeeId } }),

  get: (id: UUID) => api.get<Payslip>(`/payslips/${id}`),

  /** Only from approved runs: before that the figures are provisional. */
  mine: (params: PageParams) => api.getPage<Payslip>('/payslips/me', { params }),

  mineById: (id: UUID) => api.get<Payslip>(`/payslips/me/${id}`),
};

export const payrollAdjustmentsApi = {
  list: (periodId: UUID, employeeId?: UUID) =>
    api.get<PayrollAdjustment[]>('/payroll-adjustments', { params: { periodId, employeeId } }),

  /** Overtime sent as hours and a rate has its amount worked out server-side. */
  create: (payload: PayrollAdjustmentInput) =>
    api.post<PayrollAdjustment>('/payroll-adjustments', payload),

  update: (id: UUID, payload: PayrollAdjustmentInput) =>
    api.put<PayrollAdjustment>(`/payroll-adjustments/${id}`, payload),

  remove: (id: UUID) => api.delete<void>(`/payroll-adjustments/${id}`),
};

// --------------------------------------------------------------------- loans

export const loansApi = {
  list: (params: PageParams & { status?: LoanStatus }) =>
    api.getPage<Loan>('/loans', { params }),

  forEmployee: (employeeId: UUID) => api.get<Loan[]>(`/loans/employees/${employeeId}`),

  get: (id: UUID) => api.get<Loan>(`/loans/${id}`),

  issue: (payload: LoanInput) => api.post<Loan>('/loans', payload),

  /** Cancelling is refused once anything has been recovered — write off instead. */
  setStatus: (id: UUID, status: LoanStatus, reason: string) =>
    api.patch<Loan>(`/loans/${id}/status`, { reason }, { params: { status } }),

  /** No money moved: the repayment history is untouched. */
  writeOff: (id: UUID, payload: { amount?: number; reason: string }) =>
    api.post<Loan>(`/loans/${id}/write-off`, payload),
};

// ---------------------------------------------------------- dashboard/reports

export const hrReportsApi = {
  dashboard: () => api.get<HrDashboard>('/hr/dashboard'),

  payrollSummary: (periodId: UUID) =>
    api.get<PayrollSummary>('/hr/reports/payroll-summary', { params: { periodId } }),

  leaveSummary: (from: string, to: string) =>
    api.get<LeaveSummary>('/hr/reports/leave-summary', { params: { from, to } }),

  payrollRegisterCsv: (periodId: UUID) =>
    download('/hr/reports/payroll-register', { periodId }, 'payroll-register.csv'),

  leaveSummaryCsv: (from: string, to: string) =>
    download('/hr/reports/leave-summary.csv', { from, to }, 'leave-summary.csv'),
};

/**
 * Saves a CSV the backend generated.
 *
 * <p>Fetched as a blob rather than opened as a link: the request needs an
 * Authorization header, which a plain anchor cannot carry. The filename comes
 * from the response where the server supplied one, so the two cannot drift.
 */
async function download(path: string, params: Record<string, unknown>, fallbackName: string) {
  const response = await http.get(path, { params, responseType: 'blob' });

  const disposition = String(response.headers['content-disposition'] ?? '');
  const match = /filename="?([^"]+)"?/.exec(disposition);
  const filename = match?.[1] ?? fallbackName;

  const url = URL.createObjectURL(response.data as Blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
